import * as React from 'react';
import { BaSeButtonElement } from '../../../elements/button-element';
import { objectNotEmpty } from '../../../utils/object-utils';
import { BaSePopover, PopoverProps } from '../../alerts/popover/popover';
import { BaSeTooltip, TooltipProps } from '../../alerts/tooltip/tooltip';
import { getButtonAnchorProps } from '../button-link';
import { ButtonProps } from '../button-props';
import { BaSeIconButton, IconButtonBase } from './icon-button';
import type { IconButtonProps } from './icon-button';

type ShapeButtonElement = HTMLButtonElement | HTMLAnchorElement;

export type ShapeButtonProps = Omit<
  IconButtonProps,
  'onMouseEnter' | 'onMouseLeave'
> &
  Pick<ButtonProps, 'tooltip' | 'popover' | 'link'>;

export const BaSeShapeButton = React.forwardRef<
  BaSeButtonElement,
  ShapeButtonProps
>(
  (
    { tooltip, popover, link, isDisabled, onClick, ...iconButtonProps },
    forwardedRef,
  ) => {
    if (objectNotEmpty(tooltip) && objectNotEmpty(popover)) {
      throw Error(
        '[BaSeShapeButton] error: Informe só o "tooltip" ou o "popover"!',
      );
    }

    if (link && onClick) {
      throw Error(
        '[BaSeShapeButton] error: Informe só o "link" ou o "onClick"!',
      );
    }

    const buttonRef = React.useRef<ShapeButtonElement | null>(null);
    const anchorProps = React.useMemo(() => getButtonAnchorProps(link), [link]);

    const setButtonRef = React.useCallback(
      (node: ShapeButtonElement | null) => {
        buttonRef.current = node;
      },
      [],
    );

    React.useImperativeHandle(
      forwardedRef,
      () => ({
        focus: () => buttonRef?.current?.focus?.(),
        click: () => buttonRef?.current?.click?.(),
      }),
      [buttonRef],
    );

    const [isTooltipOrPopoverOpen, setIsTooltipOrPopoverOpen] =
      React.useState(false);
    const closeOverlayTimeoutRef = React.useRef<number | undefined>(undefined);

    const handleClick = React.useCallback(
      (event: React.MouseEvent<ShapeButtonElement>) => {
        buttonRef?.current?.focus();
        onClick?.(event as React.MouseEvent<HTMLButtonElement>);
        if (closeOverlayTimeoutRef.current !== undefined) {
          window.clearTimeout(closeOverlayTimeoutRef.current);
        }
        closeOverlayTimeoutRef.current = window.setTimeout(() => {
          if (buttonRef.current?.isConnected) {
            setIsTooltipOrPopoverOpen(false);
          }
          closeOverlayTimeoutRef.current = undefined;
        }, 1);
      },
      [onClick, buttonRef],
    );

    const overlayElement = (
      <>
        {objectNotEmpty(tooltip) && (
          <BaSeTooltip
            buddyRef={buttonRef}
            open={isTooltipOrPopoverOpen}
            {...(tooltip as Omit<TooltipProps, 'buddyRef' | 'open'>)}
          />
        )}
        {objectNotEmpty(popover) && (
          <BaSePopover
            buddyRef={buttonRef}
            open={isTooltipOrPopoverOpen}
            hasCloseButton={false}
            {...(popover as Omit<
              PopoverProps,
              'buddyRef' | 'open' | 'hasCloseButton'
            >)}
          />
        )}
      </>
    );

    React.useEffect(
      () => () => {
        if (closeOverlayTimeoutRef.current !== undefined) {
          window.clearTimeout(closeOverlayTimeoutRef.current);
        }
      },
      [],
    );

    if (anchorProps && !isDisabled) {
      return (
        <IconButtonBase
          {...iconButtonProps}
          element="anchor"
          anchorProps={anchorProps}
          buttonRef={setButtonRef}
          isDisabled={isDisabled}
          onClick={(event) => handleClick(event)}
          onMouseEnter={() => setIsTooltipOrPopoverOpen(true)}
          onMouseLeave={() => setIsTooltipOrPopoverOpen(false)}
        >
          {overlayElement}
        </IconButtonBase>
      );
    }

    return (
      <BaSeIconButton
        {...iconButtonProps}
        ref={setButtonRef}
        isDisabled={isDisabled}
        onClick={handleClick}
        onMouseEnter={() => setIsTooltipOrPopoverOpen(true)}
        onMouseLeave={() => setIsTooltipOrPopoverOpen(false)}
      >
        {overlayElement}
      </BaSeIconButton>
    );
  },
);

BaSeShapeButton.displayName = 'BaSeShapeButton';
